import * as React from "react";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createTheme, Theme } from "@mui/material/styles";
import {
  lightBlue,
  blue,
  red,
  green,
  orange,
  cyan,
} from "@mui/material/colors";

// SCSS Color Variables (restored from original _colors.scss)
const navFooterBG = "#000000";
const myColor = "rgb(11, 55, 124)";
const primaryMain = "#000000";
const navItem = "#f8f8ff";
const linkHover = "rgb(136, 226, 253)";
const pokerRed = "rgb(255, 38, 0)";
const darkRed = "rgb(190, 42, 16)";
const lightRed = "rgb(255, 36, 36)";
const pokerGreen = "rgb(2, 202, 29)";
const snowBlue = "rgb(19, 197, 197)";

// SCSS Gradients
const gradients = {
  green:
    "linear-gradient(to right bottom, #005307, #066811, #0d7d1c, #149327, #19aa33, #1bb83e, #1dc749, #1dd654, #1ae060, #15ea6c, #0ef579, #00ff85)",
  blue: "radial-gradient(circle, #cff1fd, #c6f1fe, #bcf0ff, #b2f0ff, #a8f0ff)",
  red: "radial-gradient(circle, #ffffff, #f0eaf1, #e5d5df, #debfc7, #d4abab)",
  grey: "radial-gradient(circle, rgba(246, 253, 255, 1) 27%, rgba(188, 210, 214, 1) 58%, rgba(142, 153, 156, 1) 91%)",
  greyDark:
    "radial-gradient(circle, rgba(30, 30, 30, 1) 27%, rgba(45, 45, 45, 1) 58%, rgba(60, 60, 60, 1) 91%)",
};

type ThemeMode = "light" | "dark";

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const createAppTheme = (mode: ThemeMode): Theme => {
  const isLight = mode === "light";

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: primaryMain,
        light: "#333333",
        dark: "#000000",
        contrastText: "#fff",
      },
      secondary: {
        main: snowBlue,
        light: "rgb(77, 218, 218)",
        dark: "rgb(13, 138, 138)",
        contrastText: "#000",
      },
      error: {
        main: pokerRed,
        light: lightRed,
        dark: darkRed,
        contrastText: "#fff",
      },
      success: {
        main: pokerGreen,
        light: "rgb(51, 217, 74)",
        dark: "rgb(1, 142, 20)",
        contrastText: "#000",
      },
      warning: {
        light: orange[300],
        main: orange[700],
        dark: orange[900],
      },
      info: {
        light: cyan[300],
        main: cyan[700],
        dark: cyan[900],
      },
      background: {
        default: isLight ? "rgb(246, 253, 255)" : "#121212",
        paper: isLight ? "#fff" : "#1e1e1e",
      },
      text: {
        primary: isLight ? "rgba(0, 0, 0, 0.87)" : "#ffffff",
        secondary: isLight ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.7)",
      },
      divider: isLight ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
      action: {
        hover: linkHover,
        selected: `${primaryMain}14`,
        disabled: isLight ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
      },
      // Custom properties with type assertion
      navfooter: {
        main: navFooterBG,
        contrastText: navItem,
      },
      table: {
        primary: {
          main: "#1369be",
          dark: "#0f5299",
          contrastText: "white",
        },
        secondary: {
          main: "#cd071e",
          dark: "#aa0519",
          contrastText: "white",
        },
        waitlist: {
          main: "#43e6f1",
          contrastText: "black",
        },
        currentGame: {
          main: "rgb(123, 247, 123)",
          contrastText: "black",
        },
      },
      utilities: {
        redFont: pokerRed,
        blueFont: "rgb(0, 132, 255)",
        greenFont: "rgb(138, 238, 143)",
      },
    } as any,
    typography: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      h1: {
        fontFamily: '"Anton", sans-serif',
        color: myColor,
      },
      h2: {
        fontFamily: '"Anton", sans-serif',
      },
      h3: {
        fontFamily: '"Anton", sans-serif',
      },
      h4: {
        fontFamily: '"Anton", sans-serif',
      },
      h5: {
        fontFamily: '"Anton", sans-serif',
      },
      h6: {
        fontFamily: '"Anton", sans-serif',
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 768,
        lg: 1024,
        xl: 1200,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
            textAlign: "center",
            background: isLight ? gradients.grey : gradients.greyDark,
            fontSize: {
              xs: "70%",
              sm: "100%",
            },
          },
          h1: {
            color: myColor,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            marginBottom: "10px",
            backgroundColor: isLight ? "#fff" : "#1e1e1e",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? "#fff" : "#1e1e1e",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: navFooterBG,
            position: "sticky",
            zIndex: 100,
            paddingTop: "10px",
          },
        },
      },
    },
  } as any);

  // Add custom properties to the theme
  const themeWithCustomProps = {
    ...theme,
    gradients: {
      ...gradients,
      grey: isLight ? gradients.grey : gradients.greyDark,
    },
  };

  return themeWithCustomProps as Theme;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem("themeMode") as ThemeMode;
    return savedMode || "light";
  });

  const theme = createAppTheme(mode);

  const toggleTheme = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem("themeMode", newMode);
  };

  useEffect(() => {
    localStorage.setItem("themeMode", mode);
  }, [mode]);

  const value = {
    mode,
    toggleTheme,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
